import type { FileSource } from './l3-types';
import { LabError, throwIfAborted } from './l3-errors';

export type CorpusFixture = {
  id: string;
  file: string | null;
  expected: Record<string, unknown>;
  sha256: string;
};

export type CorpusManifest = {
  schemaVersion: 1;
  fixtures: CorpusFixture[];
};

let manifestPromise: Promise<CorpusManifest> | null = null;

export function loadCorpusManifest(signal: AbortSignal): Promise<CorpusManifest> {
  if (!manifestPromise) {
    manifestPromise = fetch('/corpus/manifest.json')
      .then(async (response) => {
        if (!response.ok) throw new LabError('CORPUS_UNAVAILABLE', 'corpus-load', `Unable to load generated corpus: ${response.status}.`);
        return response.json() as Promise<CorpusManifest>;
      });
  }
  throwIfAborted(signal, 'corpus-load');
  return manifestPromise;
}

export async function loadFixtureSource(id: string, signal: AbortSignal): Promise<FileSource> {
  const manifest = await loadCorpusManifest(signal);
  const fixture = manifest.fixtures.find((candidate) => candidate.id === id);
  if (!fixture?.file) throw new LabError('FIXTURE_UNAVAILABLE', 'corpus-load', `Fixture ${id} is unavailable in the generated corpus.`);
  if (id === 'V-LIMIT') {
    const minimumBytes = Number(fixture.expected.minimumBytes);
    if (!Number.isSafeInteger(minimumBytes) || minimumBytes <= 64 * 1024 * 1024) throw new LabError('FIXTURE_INVALID', 'corpus-load', 'V-LIMIT must exceed the image admission limit.');
    return { id, mimeType: 'application/octet-stream', data: new ArrayBuffer(minimumBytes) };
  }
  const response = await fetch(`/corpus/${fixture.file}`, { signal });
  if (!response.ok) throw new LabError('FIXTURE_UNAVAILABLE', 'corpus-load', `Unable to load fixture ${id}: ${response.status}.`);
  const data = await response.arrayBuffer();
  throwIfAborted(signal, 'corpus-load');
  return { id, mimeType: response.headers.get('content-type') ?? 'application/octet-stream', data };
}
